import { useCallback, useEffect, useMemo, useState } from "react";
import { useHistory, useLocation } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Collapse,
  IconButton,
  MenuItem,
  Stack,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import KeyboardArrowUpIcon from "@mui/icons-material/KeyboardArrowUp";

import { Page } from "v2/components/Page";
import { StatusBadge } from "v2/components/StatusBadge";
import { formatSaoPauloDateTime, formatUtcTooltip } from "./dates";
import { ICnpjHealth } from "./contracts/types";
import {
  computeCauseAggregates,
  computeCounters,
  countVisibleByStatus,
  delayRank,
  flattenActivities,
  hasUncoveredProblem,
  isActionableToday,
  pickLeadingActivity,
  resolveCnpjContractStatus,
  sortBySeverity,
  worstDaysSinceLastSuccessForCnpj,
} from "./aggregate";
import { describeActivity, describeQbem, formatFederalTaxId, formatWorstDelay } from "./format";
import {
  getPortfolio,
  isSourceUnavailableSimulationEnabled,
  isSourceUnavailableSimulationSupported,
  setSimulateSourceUnavailable,
} from "./operational-panel.service";
import { useContractStatus } from "./useContractStatus";

const QUERY_KEY = ["operational-panel", "portfolio"];

export function PortfolioPage() {
  const history = useHistory();
  const location = useLocation();

  const initialParams = useMemo(() => new URLSearchParams(location.search), []); // eslint-disable-line react-hooks/exhaustive-deps

  const [seller, setSeller] = useState(initialParams.get("seller") ?? "");
  const [search, setSearch] = useState(initialParams.get("q") ?? "");
  const [activityFilter, setActivityFilter] = useState(initialParams.get("activity") ?? "");
  const [minDelayDays, setMinDelayDays] = useState(initialParams.get("minDelay") ?? "");
  const [showAll, setShowAll] = useState(initialParams.get("all") === "1");
  const [simulateStale, setSimulateStale] = useState(isSourceUnavailableSimulationEnabled());

  useEffect(() => {
    const next = new URLSearchParams();
    if (seller) next.set("seller", seller);
    if (search) next.set("q", search);
    if (activityFilter) next.set("activity", activityFilter);
    if (minDelayDays) next.set("minDelay", minDelayDays);
    if (showAll) next.set("all", "1");
    const search2 = next.toString();
    const nextSearch = search2 ? `?${search2}` : "";
    if (nextSearch !== location.search) {
      history.replace({ pathname: location.pathname, search: nextSearch });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [seller, search, activityFilter, minDelayDays, showAll]);

  const { data, isLoading, isError, dataUpdatedAt, refetch } = useQuery({
    queryKey: QUERY_KEY,
    queryFn: getPortfolio,
    refetchInterval: 5000,
  });

  const {
    getContractStatus,
    sessionOverrides: contractSessionOverrides,
    generateCodeSnippet: generateContractCodeSnippet,
  } = useContractStatus();

  // Ponto 3 do plano de melhorias de lógica (2026-08-10): status por
  // workspace, rolado pro CNPJ via resolveCnpjContractStatus.
  const getCnpjContractStatus = useCallback(
    (cnpj: ICnpjHealth) =>
      resolveCnpjContractStatus(
        cnpj.workspaces.map((workspace) => getContractStatus(workspace.workspaceId, cnpj.sellerName, cnpj.federalTaxId))
      ),
    [getContractStatus]
  );

  const sellerOptions = useMemo(() => {
    if (!data) return [];
    return Array.from(new Set(data.cnpjs.map((cnpj) => cnpj.sellerName))).sort();
  }, [data]);

  // F0.2 §"Filtros que valem": "por atividade — quando já se sabe qual DAG quebrou".
  const activityOptions = useMemo(() => {
    if (!data) return [];
    const names = new Set<string>();
    data.cnpjs.forEach((cnpj) =>
      cnpj.workspaces.forEach((workspace) => workspace.activities.forEach((activity) => names.add(activity.activityName)))
    );
    return Array.from(names).sort();
  }, [data]);

  const filteredCnpjs = useMemo(() => {
    if (!data) return [];
    const term = search.trim().toLowerCase();
    const termDigits = term.replace(/\D/g, "");
    const minDelay = minDelayDays ? Number(minDelayDays) : null;

    return data.cnpjs.filter((cnpj) => {
      if (seller && cnpj.sellerName !== seller) return false;
      if (
        activityFilter &&
        !cnpj.workspaces.some((workspace) => workspace.activities.some((activity) => activity.activityName === activityFilter))
      ) {
        return false;
      }
      if (minDelay !== null) {
        // T3: "nunca teve sucesso" é o pior atraso possível e tem de passar em
        // qualquer limiar — antes ele era `undefined` e o filtro o descartava,
        // escondendo exatamente os casos mais graves.
        if (delayRank(worstDaysSinceLastSuccessForCnpj(cnpj)) < minDelay) return false;
      }
      // Ponto 3: INATIVO some da tela inteira, não entra em contador nenhum.
      if (getCnpjContractStatus(cnpj) === "INATIVO") return false;
      if (!term) return true;
      const matchesName = cnpj.buyerName.toLowerCase().includes(term);
      const matchesDoc = termDigits.length > 0 && cnpj.federalTaxId.includes(termDigits);
      return matchesName || matchesDoc;
    });
  }, [data, seller, search, activityFilter, minDelayDays, getCnpjContractStatus]);

  // Ponto 3: "em configuração" some do caminho de leitura normal (não conta
  // como problema, não aparece na lista padrão) mas fica acessível numa
  // seção colapsável própria — ver JSX abaixo. `normalCnpjs` (ATIVO) é quem
  // alimenta contadores/agregação/individualProblems/allVisible, do mesmo
  // jeito que `filteredCnpjs` alimentava antes desta mudança.
  const emConfiguracaoCnpjs = useMemo(
    () => filteredCnpjs.filter((cnpj) => getCnpjContractStatus(cnpj) === "EM_CONFIGURACAO"),
    [filteredCnpjs, getCnpjContractStatus]
  );
  const normalCnpjs = useMemo(
    () => filteredCnpjs.filter((cnpj) => getCnpjContractStatus(cnpj) !== "EM_CONFIGURACAO"),
    [filteredCnpjs, getCnpjContractStatus]
  );

  const flatEntries = useMemo(() => flattenActivities(normalCnpjs), [normalCnpjs]);
  const counters = useMemo(() => computeCounters(normalCnpjs), [normalCnpjs]);
  const causeAggregates = useMemo(() => computeCauseAggregates(normalCnpjs), [normalCnpjs]);
  const aggregateActivityNames = useMemo(() => new Set(causeAggregates.map((item) => item.activityName)), [causeAggregates]);

  const semImplantacaoVisible = countVisibleByStatus(flatEntries, "NUNCA_RODOU");
  const semTrilhaVisible = countVisibleByStatus(flatEntries, "SEM_DADO");

  const individualProblems = useMemo(() => {
    const problems = normalCnpjs.filter(
      (cnpj) => isActionableToday(cnpj.status) && hasUncoveredProblem(cnpj, aggregateActivityNames, isActionableToday)
    );
    return sortBySeverity(problems, (cnpj) => cnpj.status);
  }, [normalCnpjs, aggregateActivityNames]);

  // Faixa de veredito: conta o que de fato aparece na tela (agregados + individuais),
  // não o total bruto de CNPJs acionáveis — senão o número não bate com as linhas mostradas.
  const problemsCount = causeAggregates.length + individualProblems.length;

  const allVisible = useMemo(() => sortBySeverity(normalCnpjs, (cnpj) => cnpj.status), [normalCnpjs]);

  // Separado do resto: "Ver todos" ordenado só por severidade enterrava os
  // saudáveis no fim de listas longas, dando a impressão de que estava tudo
  // quebrado. Aqui os dois grupos ficam visualmente distintos.
  //
  // A mesma agregação por causa da view padrão (F0.2) se aplica aqui — sem
  // isso, "Ver todos" listava as N linhas de um mesmo incidente (ex.: 23
  // "CLIENTE RETORNO") soltas, duplicando o que já aparece na linha
  // agregada acima e reproduzindo o "muro vermelho" que a agregação existe
  // pra evitar. Quem está coberto por uma agregação só aparece de novo se
  // expandir a linha do causeAggregates (ver `expandedAggregates`).
  const unhealthyVisible = useMemo(
    () =>
      allVisible.filter(
        (cnpj) => cnpj.status !== "SUCCESS" && hasUncoveredProblem(cnpj, aggregateActivityNames, (status) => status !== "SUCCESS")
      ),
    [allVisible, aggregateActivityNames]
  );
  const healthyVisible = useMemo(() => allVisible.filter((cnpj) => cnpj.status === "SUCCESS"), [allVisible]);
  const [healthySectionOpen, setHealthySectionOpen] = useState(true);
  const [expandedAggregates, setExpandedAggregates] = useState<Set<string>>(new Set());
  const [ocultasSectionOpen, setOcultasSectionOpen] = useState(false);
  const [emConfiguracaoSectionOpen, setEmConfiguracaoSectionOpen] = useState(false);

  function toggleAggregateExpanded(activityName: string) {
    setExpandedAggregates((current) => {
      const next = new Set(current);
      if (next.has(activityName)) {
        next.delete(activityName);
      } else {
        next.add(activityName);
      }
      return next;
    });
  }

  function goToCnpj(federalTaxId: string) {
    history.push(`/v2/painel-operacional/cnpj/${federalTaxId}`);
  }

  function toggleSimulateStale() {
    const next = !simulateStale;
    setSimulateStale(next);
    setSimulateSourceUnavailable(next);
    refetch();
  }

  // Extraído pra reordenar por modo: em "Ver todos" os saudáveis vêm antes
  // dos problemas (também antes da linha agregada — é problema igual a
  // qualquer outro); na visão padrão a agregada continua primeiro, como
  // sempre foi, já que não há seção de saudáveis ali pra vir na frente.
  const causeAggregatesBlock = causeAggregates.map((aggregate) => {
    const isExpanded = expandedAggregates.has(aggregate.activityName);
    return (
      <Box key={aggregate.activityName} sx={{ py: 1.5, borderBottom: "1px solid", borderColor: "divider" }}>
        <Stack direction="row" spacing={1} alignItems="center">
          <StatusBadge status="FAILED" label={aggregate.activityName} />
          <Typography variant="body2">
            falhou em {aggregate.failedCount} de {aggregate.totalCount} workspaces
          </Typography>
        </Stack>
        <Stack direction="row" spacing={1} alignItems="center" sx={{ mt: 0.5 }}>
          <Typography variant="body2" color="text.secondary">
            {aggregate.affectedBuyerNames.slice(0, 2).join(", ")}
            {aggregate.affectedBuyerNames.length > 2 ? `, +${aggregate.affectedBuyerNames.length - 2} clientes` : ""}
          </Typography>
          <Button size="small" onClick={() => toggleAggregateExpanded(aggregate.activityName)}>
            {isExpanded ? "recolher" : "expandir"}
          </Button>
        </Stack>
        <Collapse in={isExpanded}>
          <Box sx={{ mt: 1, ml: 2, borderLeft: "2px solid", borderColor: "divider", pl: 2 }}>
            {/*
              Sem `aggregateActivityNames` de propósito: aqui a lista É a
              expansão de uma causa agregada, então repetir a causa é o ponto
              da tela, não o defeito que a T10 corrige.
            */}
            <CnpjRowHeader />
            {aggregate.affectedCnpjs.map((cnpj) => (
              <CnpjRow key={cnpj.federalTaxId} cnpj={cnpj} onOpen={() => goToCnpj(cnpj.federalTaxId)} />
            ))}
          </Box>
        </Collapse>
      </Box>
    );
  });

  // Extraído pelo mesmo motivo de causeAggregatesBlock acima: precisa
  // aparecer depois de "Saudáveis" no "Ver todos" (onde a seção Saudáveis
  // existe), mas ainda aparecer sozinho na visão padrão (onde não existe
  // seção de saudáveis pra vir antes).
  const emConfiguracaoBlock = emConfiguracaoCnpjs.length > 0 && (
    <Box sx={{ mb: 3 }}>
      <Stack
        direction="row"
        spacing={1}
        alignItems="center"
        sx={{ py: 1, cursor: "pointer" }}
        onClick={() => setEmConfiguracaoSectionOpen((value) => !value)}
      >
        <StatusBadge status="NUNCA_RODOU" label={`${emConfiguracaoCnpjs.length} em configuração`} />
        <IconButton size="small">
          {emConfiguracaoSectionOpen ? <KeyboardArrowUpIcon fontSize="small" /> : <KeyboardArrowDownIcon fontSize="small" />}
        </IconButton>
      </Stack>
      <Collapse in={emConfiguracaoSectionOpen}>
        <CnpjRowHeader />
        {emConfiguracaoCnpjs.map((cnpj) => (
          <CnpjRow
            key={cnpj.federalTaxId}
            cnpj={cnpj}
            onOpen={() => goToCnpj(cnpj.federalTaxId)}
            aggregateActivityNames={aggregateActivityNames}
          />
        ))}
      </Collapse>
    </Box>
  );

  return (
    <>
    <Page title="Rotinas">
      <Box sx={{ p: 3 }}>
        <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 2 }}>
          <Typography variant="h5">Rotinas</Typography>
          <Stack direction="row" spacing={1} alignItems="center">
            {isSourceUnavailableSimulationSupported() && (
              <Button size="small" color={simulateStale ? "error" : "inherit"} onClick={toggleSimulateStale}>
                {simulateStale ? "Simulando fonte indisponível" : "Simular fonte indisponível"}
              </Button>
            )}
            {dataUpdatedAt > 0 && !isError && (
              <Tooltip title={formatUtcTooltip(new Date(dataUpdatedAt).toISOString())}>
                <Typography variant="body2" color="text.secondary">
                  Dados atualizados em {formatSaoPauloDateTime(new Date(dataUpdatedAt).toISOString())}
                </Typography>
              </Tooltip>
            )}
          </Stack>
        </Stack>

        {isLoading && (
          <Stack alignItems="center" sx={{ py: 6 }}>
            <CircularProgress size={28} />
          </Stack>
        )}

        {isError && (
          <Alert severity="warning" sx={{ mb: 2 }}>
            Não foi possível confirmar o estado atual (Airflow ou Postgres indisponível). O que aparece abaixo pode
            estar desatualizado — nunca leia como "tudo certo".
          </Alert>
        )}

        {data && (
          <>
            <Stack direction="row" spacing={3} alignItems="baseline" sx={{ mb: 3 }}>
              <Typography variant="h6" color={problemsCount > 0 ? "error.main" : "text.primary"}>
                {problemsCount} {problemsCount === 1 ? "problema exige" : "problemas exigem"} ação
              </Typography>
              <Typography variant="body1" color="text.secondary">
                {counters.saudaveis} CNPJs saudáveis
              </Typography>
            </Stack>

            <Stack direction="row" spacing={2} alignItems="center" flexWrap="wrap" useFlexGap sx={{ mb: 3, rowGap: 1.5 }}>
              <TextField
                select
                size="small"
                label="Carteira"
                value={seller}
                onChange={(event) => setSeller(event.target.value)}
                sx={{ minWidth: 220 }}
                SelectProps={{ displayEmpty: true }}
              >
                <MenuItem value="">Todas as carteiras</MenuItem>
                {sellerOptions.map((option) => (
                  <MenuItem key={option} value={option}>
                    {option}
                  </MenuItem>
                ))}
              </TextField>
              <TextField
                select
                size="small"
                label="Atividade"
                value={activityFilter}
                onChange={(event) => setActivityFilter(event.target.value)}
                sx={{ minWidth: 200 }}
                SelectProps={{ displayEmpty: true }}
              >
                <MenuItem value="">Todas as atividades</MenuItem>
                {activityOptions.map((option) => (
                  <MenuItem key={option} value={option}>
                    {option}
                  </MenuItem>
                ))}
              </TextField>
              <TextField
                size="small"
                label="CNPJ ou razão social"
                value={search}
                onChange={(event) => setSearch(event.target.value)}
                sx={{ minWidth: 280 }}
              />
              <TextField
                select
                size="small"
                label="Atraso mínimo"
                value={minDelayDays}
                onChange={(event) => setMinDelayDays(event.target.value)}
                sx={{ minWidth: 180 }}
                SelectProps={{ displayEmpty: true }}
              >
                <MenuItem value="">Qualquer</MenuItem>
                <MenuItem value="1">≥ 1 dia</MenuItem>
                <MenuItem value="3">≥ 3 dias</MenuItem>
                <MenuItem value="7">≥ 7 dias</MenuItem>
                <MenuItem value="30">≥ 30 dias</MenuItem>
              </TextField>
              <Button
                variant="contained"
                size="small"
                onClick={() => setShowAll((value) => !value)}
                sx={{ whiteSpace: "nowrap", flexShrink: 0, height: 40, borderRadius: 1 }}
              >
                {showAll ? "Só problemas" : "Ver todos"}
              </Button>
            </Stack>

            {!showAll && emConfiguracaoBlock}

            {!showAll && causeAggregatesBlock}

            {!showAll && individualProblems.length > 0 && <CnpjRowHeader />}

            {!showAll &&
              individualProblems.map((cnpj) => (
                <CnpjRow
                  key={cnpj.federalTaxId}
                  cnpj={cnpj}
                  onOpen={() => goToCnpj(cnpj.federalTaxId)}
                  aggregateActivityNames={aggregateActivityNames}
                />
              ))}

            {/*
              F0.5 (spec, item em aberto): "0 problemas" e "0 CNPJs saudáveis" batiam
              juntos tanto quando a carteira estava genuinamente saudável quanto quando
              não havia dado nenhum — mesmo banner verde nos dois casos. Três estados
              agora, não um: sem dado nenhum na fonte, filtro sem resultado, ou saudável
              de verdade.
            */}
            {!showAll && individualProblems.length === 0 && causeAggregates.length === 0 && (
              <>
                {data.cnpjs.length === 0 ? (
                  <Alert severity="info" sx={{ mt: 2 }}>
                    Nenhum CNPJ carregado. Isto não é "tudo saudável" — é ausência de dado da fonte.
                  </Alert>
                ) : filteredCnpjs.length === 0 ? (
                  <Alert severity="info" sx={{ mt: 2 }}>
                    Nenhum CNPJ encontrado para esse filtro. Tente limpar a carteira, a busca, a atividade ou o atraso mínimo.
                  </Alert>
                ) : (
                  <Alert severity="success" sx={{ mt: 2 }}>
                    Nenhum problema exige ação agora.
                  </Alert>
                )}
              </>
            )}

            {showAll && healthyVisible.length > 0 && (
              <Box sx={{ mb: causeAggregates.length > 0 || unhealthyVisible.length > 0 ? 3 : 0 }}>
                <Stack
                  direction="row"
                  spacing={1}
                  alignItems="center"
                  sx={{ py: 1, cursor: "pointer" }}
                  onClick={() => setHealthySectionOpen((value) => !value)}
                >
                  <StatusBadge status="SUCCESS" label={`${healthyVisible.length} saudáveis`} />
                  <IconButton size="small">
                    {healthySectionOpen ? <KeyboardArrowUpIcon fontSize="small" /> : <KeyboardArrowDownIcon fontSize="small" />}
                  </IconButton>
                </Stack>
                <Collapse in={healthySectionOpen}>
                  <CnpjRowHeader />
                  {healthyVisible.map((cnpj) => (
                    <CnpjRow
                      key={cnpj.federalTaxId}
                      cnpj={cnpj}
                      onOpen={() => goToCnpj(cnpj.federalTaxId)}
                      aggregateActivityNames={aggregateActivityNames}
                    />
                  ))}
                </Collapse>
              </Box>
            )}

            {showAll && emConfiguracaoBlock}

            {showAll && causeAggregatesBlock}

            {showAll && unhealthyVisible.length > 0 && <CnpjRowHeader />}

            {showAll &&
              unhealthyVisible.map((cnpj) => (
                <CnpjRow
                  key={cnpj.federalTaxId}
                  cnpj={cnpj}
                  onOpen={() => goToCnpj(cnpj.federalTaxId)}
                  aggregateActivityNames={aggregateActivityNames}
                />
              ))}

            <Stack direction="row" spacing={2} alignItems="center" sx={{ mt: 3, pt: 2, borderTop: "1px solid", borderColor: "divider" }}>
              <Tooltip
                title={
                  'Nunca rodou. Mistura cliente em implantação (temporário, normal) com configuração ' +
                  'órfã esquecida (permanente) — só quem conhece o cliente distingue. Ação: implantação/CS, ' +
                  'no ciclo deles, não hoje.'
                }
              >
                <Chip size="small" label={`${semImplantacaoVisible} sem implantação`} />
              </Tooltip>
              <Tooltip
                title={
                  'Sem trilha de dado — não temos como observar essa atividade, não é "está tudo bem". ' +
                  "Lacuna de instrumentação conhecida (ex.: exportação sem log-fonte), não incidente do dia. " +
                  "Ação: engenharia, backlog."
                }
              >
                <Chip size="small" label={`${semTrilhaVisible} sem trilha`} />
              </Tooltip>
            </Stack>

            {/*
              T11 (plano v2, 2026-08-11) — o status de contrato marcado na UI
              parava de evaporar em silêncio.

              O dropdown de contrato (em Cadastro) grava em `localStorage`:
              vale num navegador só e some ao limpar cache. O mecanismo previsto
              pra promover a marcação a `CONTRACT_STATUS_OVERRIDES` via PR —
              `useContractStatus.generateCodeSnippet` — existia e **nunca era
              chamado**: o botão "copiar como código" desta tela estava ligado
              ao snippet de silenciamento, funcionalidade removida em
              2026-08-11 por ser inalcançável na prática.

              Decidido em 2026-08-11: sem tabela nova, sem endpoint. O arquivo
              versionado continua sendo o mecanismo, e mudar status exige PR +
              deploy. O defeito real nunca foi a marcação ser local — foi ela
              **parecer** definitiva. Daí o texto dizendo o que a marcação de
              sessão é.
            */}
            {contractSessionOverrides.length > 0 && (
              <Box sx={{ mt: 2 }}>
                <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap>
                  <Typography variant="body2" color="text.secondary">
                    {contractSessionOverrides.length} marcação(ões) de contrato valem <strong>só neste navegador</strong>{" "}
                    até virarem PR em <code>contractStatus.ts</code>.
                  </Typography>
                  <Button
                    size="small"
                    startIcon={<ContentCopyIcon fontSize="small" />}
                    onClick={() => navigator.clipboard.writeText(generateContractCodeSnippet())}
                  >
                    Copiar marcações de contrato como código
                  </Button>
                </Stack>
              </Box>
            )}
          </>
        )}
      </Box>
    </Page>

    </>
  );
}

/** Grade compartilhada por `CnpjRowHeader` e `CnpjRow` — uma definição só, pra não sair de alinhamento. */
const CNPJ_ROW_GRID = {
  xs: "1fr",
  // A coluna do CNPJ é 168px porque `00.000.000/0000-00` formatado não cabe
  // em menos: a 150px ele quebra em duas linhas e desalinha a linha inteira.
  // O espaço saiu de "Estado" e "Atraso", que têm conteúdo curto e previsível.
  // "Estado" é 152px: o chip de "Em andamento" — o rótulo mais longo dos sete
  // — precisa disso com ícone e padding. A 132px ele truncava.
  md: "minmax(130px, 1.4fr) 168px minmax(90px, 0.8fr) 152px minmax(160px, 2fr) 64px",
} as const;

/**
 * Seis colunas, uma informação cada. Duas mudanças de 2026-08-11:
 *
 * 1. **"Estado" ganhou coluna própria.** Antes o estado vinha embutido no
 *    `StatusBadge` que também carregava o nome do cliente — um chip colorido
 *    de largura variável conforme o tamanho do nome. Como a primeira coluna
 *    nunca tinha a mesma largura duas vezes, nada alinhava, e a lista lia
 *    como um muro de blocos coloridos em vez de uma tabela.
 *
 * 2. **"O que quebrou" virou "Situação".** O título mentia: a coluna mostra a
 *    atividade em destaque de *qualquer* linha, inclusive das verdes, então
 *    um CNPJ saudável aparecia com "Exportação" sob a pergunta "o que
 *    quebrou". "Situação" é verdadeiro para os sete estados.
 */
const CNPJ_ROW_COLUMNS = ["Cliente", "CNPJ", "Corretora", "Estado", "Situação", "Atraso"];

function CnpjRowHeader() {
  return (
    <Box
      sx={{
        display: { xs: "none", md: "grid" },
        gridTemplateColumns: CNPJ_ROW_GRID,
        columnGap: 2,
        pb: 0.5,
        borderBottom: "1px solid",
        borderColor: "divider",
      }}
    >
      {CNPJ_ROW_COLUMNS.map((column, index) => (
        <Typography
          key={column}
          variant="caption"
          color="text.secondary"
          sx={{
            textTransform: "uppercase",
            letterSpacing: 0.6,
            textAlign: index === CNPJ_ROW_COLUMNS.length - 1 ? "right" : "left",
          }}
        >
          {column}
        </Typography>
      ))}
    </Box>
  );
}

interface ICnpjRowProps {
  cnpj: ICnpjHealth;
  onOpen: () => void;
  /** T10: sem isto a linha exibe justamente a causa que a linha agregada acima já resumiu. */
  aggregateActivityNames?: Set<string>;
}

/**
 * T9 (plano v2, 2026-08-11) — a linha identifica o CNPJ.
 *
 * Antes eram duas linhas de texto livre com `buyerName` sozinho no badge. A
 * Galcorr tem 6 CNPJs e a Enesa 10: a tela mostrava seis linhas "GALCORR ·
 * Galcorr" visualmente idênticas, que eram clientes diferentes. Não havia como
 * saber qual abrir, e o operador concluía que a tela estava repetindo quando
 * estava listando coisas distintas mal rotuladas.
 *
 * Agora são colunas fixas alinhadas: cliente · CNPJ · corretora · o que
 * quebrou · atraso. Agrupar por causa e agrupar por cliente foram considerados
 * e descartados — a lista plana com identidade correta resolve o problema
 * relatado sem reescrever a hierarquia da página.
 */
function CnpjRow({ cnpj, onOpen, aggregateActivityNames }: ICnpjRowProps) {
  const leadingActivity = pickLeadingActivity(cnpj, aggregateActivityNames);
  const delay = formatWorstDelay(worstDaysSinceLastSuccessForCnpj(cnpj));
  const qbemMetrics = leadingActivity ? describeQbem(leadingActivity) : null;

  return (
    <Box
      sx={{
        display: "grid",
        gridTemplateColumns: CNPJ_ROW_GRID,
        alignItems: "center",
        columnGap: 2,
        rowGap: 0.5,
        py: 1.25,
        borderBottom: "1px solid",
        borderColor: "divider",
        cursor: "pointer",
        // A linha inteira é o alvo de clique, mas nada dizia isso: só o cursor
        // mudava, e cursor não aparece em toque nem em varredura visual. O
        // nome do cliente é quem responde, porque é o que o operador está
        // procurando quando decide abrir a linha.
        "&:hover": {
          bgcolor: "action.hover",
          "& .CnpjRow-name": {
            color: "primary.main",
            transform: "scale(1.03)",
          },
        },
      }}
      onClick={onOpen}
    >
      <Typography
        className="CnpjRow-name"
        variant="body2"
        fontWeight={500}
        noWrap
        title={cnpj.buyerName}
        sx={{
          // `transformOrigin` à esquerda: crescendo a partir do centro, o nome
          // invadiria a coluna do CNPJ e a linha pareceria tremer.
          transformOrigin: "left center",
          transition: "color 150ms ease-out, transform 150ms ease-out",
          "@media (prefers-reduced-motion: reduce)": {
            transition: "color 150ms ease-out",
            transform: "none !important",
          },
        }}
      >
        {cnpj.buyerName}
      </Typography>

      <Typography variant="body2" color="text.secondary" noWrap sx={{ fontVariantNumeric: "tabular-nums" }}>
        {formatFederalTaxId(cnpj.federalTaxId)}
      </Typography>

      <Typography variant="body2" color="text.secondary" noWrap title={cnpj.sellerName}>
        {cnpj.sellerName}
      </Typography>

      {/*
        `StatusBadge` sem `label`, então ele usa o nome do próprio estado. O
        ícone é o ponto principal: numa lista longa o operador varre por forma
        antes de ler — X, relógio, ampulheta e seta circular dizem a urgência
        antes da palavra. Um ponto colorido, que era o que estava aqui, joga
        todo o peso na cor e não sobrevive a daltonismo nem a leitura rápida.

        Agora o estado ocupa coluna própria e o nome do cliente ficou de fora
        do chip — que era o defeito original: o badge crescia junto com o nome
        do cliente e desalinhava a tabela inteira.
      */}
      <Box sx={{ minWidth: 0 }}>
        <StatusBadge status={cnpj.status} />
      </Box>

      <Stack direction="row" spacing={1} alignItems="center" sx={{ minWidth: 0 }}>
        <Box sx={{ minWidth: 0 }}>
          <Typography variant="body2" noWrap title={leadingActivity ? describeActivity(leadingActivity) : undefined}>
            {leadingActivity ? describeActivity(leadingActivity) : "—"}
          </Typography>
          {qbemMetrics && (
            <Typography variant="caption" color="text.secondary" noWrap display="block">
              {qbemMetrics}
            </Typography>
          )}
        </Box>
      </Stack>

      <Typography
        variant="body2"
        color={delay === "nunca" ? "error.main" : "text.secondary"}
        sx={{ textAlign: { xs: "left", md: "right" }, fontVariantNumeric: "tabular-nums" }}
      >
        {delay ?? "—"}
      </Typography>
    </Box>
  );
}
